import React, { useState } from 'react';
import { Globe, Lock, AlertTriangle, Info, Eye, EyeOff, Shield, Users, Building2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';

// ============================================
// VISIBILITY TOOLTIP
// ============================================

interface VisibilityTooltipProps {
  isPublic: boolean;
  children: React.ReactNode;
}

export function VisibilityTooltip({ isPublic, children }: VisibilityTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {children}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-72 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg -top-2 left-full ml-2">
          <div className="flex items-start gap-2">
            {isPublic ? (
              <>
                <Globe className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-400">Template Público</p>
                  <p className="text-gray-300 mt-1">
                    Visível para todos os Tenant Admins no marketplace de templates.
                    Pode ser instanciado por qualquer tenant.
                  </p>
                </div>
              </>
            ) : (
              <>
                <Lock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-400">Template Privado</p>
                  <p className="text-gray-300 mt-1">
                    Visível apenas para Super Admins. Não aparece no marketplace
                    para Tenant Admins.
                  </p>
                </div>
              </>
            )}
          </div>
          
          {/* Arrow */}
          <div className="absolute top-4 -left-2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-gray-900" />
        </div>
      )}
    </div>
  );
}

// ============================================
// VISIBILITY EXPLANATION MODAL
// ============================================

interface VisibilityExplanationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function VisibilityExplanationModal({ isOpen, onClose }: VisibilityExplanationModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Entendendo a Visibilidade de Templates"
      size="lg"
    >
      <div className="space-y-6">
        {/* Public Templates */}
        <div className="border border-green-200 rounded-lg p-4 bg-green-50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-green-900">Templates Públicos</h3>
              <p className="text-sm text-green-700 mt-1">
                Templates públicos são visíveis para todos os Tenant Admins da plataforma.
              </p>
              
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <Users className="w-4 h-4" />
                  <span>Visível no marketplace para todos os tenants</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <Building2 className="w-4 h-4" />
                  <span>Pode ser instanciado por qualquer tenant</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <Eye className="w-4 h-4" />
                  <span>Documentação e exemplos acessíveis a todos</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Private Templates */}
        <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-medium text-yellow-900">Templates Privados</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Templates privados são visíveis apenas para Super Admins.
              </p>
              
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-yellow-800">
                  <EyeOff className="w-4 h-4" />
                  <span>Não aparece no marketplace de tenants</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-yellow-800">
                  <Shield className="w-4 h-4" />
                  <span>Ideal para templates em desenvolvimento</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-yellow-800">
                  <Lock className="w-4 h-4" />
                  <span>Pode conter credenciais ou configs sensíveis</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Implications */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Implicações de Segurança
          </h3>
          <ul className="mt-3 space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-gray-400">•</span>
              <span>Templates públicos não devem conter credenciais hardcoded</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-400">•</span>
              <span>Revise a documentação antes de tornar público</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-400">•</span>
              <span>Exemplos de código devem usar placeholders para dados sensíveis</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-400">•</span>
              <span>Considere o impacto no suporte ao tornar público</span>
            </li>
          </ul>
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose}>Entendi</Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// VISIBILITY CONFIRMATION DIALOG
// ============================================

interface VisibilityConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentVisibility: boolean;
  templateName: string;
}

export function VisibilityConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  currentVisibility,
  templateName,
}: VisibilityConfirmationDialogProps) {
  const isGoingPublic = !currentVisibility;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isGoingPublic ? 'Tornar Template Público?' : 'Tornar Template Privado?'}
    >
      <div className="space-y-4">
        {isGoingPublic ? (
          <>
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Atenção</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Ao tornar <strong>{templateName}</strong> público, ele ficará visível
                  para todos os Tenant Admins da plataforma.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Isso significa que:</p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  O template aparecerá no marketplace
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  Qualquer tenant poderá criar instâncias
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  A documentação será acessível a todos
                </li>
              </ul>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Informação</p>
                <p className="text-sm text-blue-700 mt-1">
                  Ao tornar <strong>{templateName}</strong> privado, ele ficará visível
                  apenas para Super Admins.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Isso significa que:</p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  O template será removido do marketplace
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  Instâncias existentes continuarão funcionando
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  Novos tenants não poderão criar instâncias
                </li>
              </ul>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant={isGoingPublic ? 'primary' : 'secondary'}
          >
            {isGoingPublic ? 'Tornar Público' : 'Tornar Privado'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// VISIBILITY BADGE
// ============================================

interface VisibilityBadgeProps {
  isPublic: boolean;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  onClick?: () => void;
}

export function VisibilityBadge({ 
  isPublic, 
  size = 'md', 
  showLabel = true,
  onClick 
}: VisibilityBadgeProps) {
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  
  const content = (
    <div className={`inline-flex items-center gap-1 ${onClick ? 'cursor-pointer' : ''}`}>
      {isPublic ? (
        <>
          <Globe className={`${iconSize} text-green-600`} />
          {showLabel && <span className="text-green-700">Público</span>}
        </>
      ) : (
        <>
          <Lock className={`${iconSize} text-yellow-600`} />
          {showLabel && <span className="text-yellow-700">Privado</span>}
        </>
      )}
    </div>
  );

  if (onClick) {
    return (
      <Badge
        variant={isPublic ? 'success' : 'warning'}
        className={`${size === 'sm' ? 'text-xs px-1.5 py-0.5' : ''} cursor-pointer hover:opacity-80`}
        onClick={onClick}
      >
        {content}
      </Badge>
    );
  }

  return (
    <Badge
      variant={isPublic ? 'success' : 'warning'}
      className={size === 'sm' ? 'text-xs px-1.5 py-0.5' : ''}
    >
      {content}
    </Badge>
  );
}

// ============================================
// VISIBILITY FILTER
// ============================================

interface VisibilityFilterProps {
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
}

export function VisibilityFilter({ value, onChange }: VisibilityFilterProps) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-600">Visibilidade:</label>
      <select
        value={value === undefined ? 'all' : value ? 'public' : 'private'}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === 'all' ? undefined : val === 'public');
        }}
        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="all">Todos</option>
        <option value="public">Públicos</option>
        <option value="private">Privados</option>
      </select>
    </div>
  );
}

export default {
  VisibilityTooltip,
  VisibilityExplanationModal,
  VisibilityConfirmationDialog,
  VisibilityBadge,
  VisibilityFilter,
};
